import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import "./App.css";
import Cookies from "js-cookie";
import LanguageSwitcher from "./components/LanguageSwitcher";

interface University {
  id: number;
  name: string;
  shortName: string;
  homepage: string;
  icon?: string;
}

interface Course {
  id: number;
  name: string;
  url: string;
  description: string;
}

// Helper to get API URL with host from env
const API_HOST = process.env.REACT_APP_API_HOST || "";
function apiUrl(path: string) {
  // Ensure no double slashes
  return `${API_HOST}${path.startsWith("/") ? path : "/" + path}`;
}

// Add logo import from public folder
const logo192 = process.env.PUBLIC_URL + "/logo192.png";
const COUNSELOR_PHONE = "(+61) 402 704 438";
const COUNSELOR_EMAIL = "hoang.le@mugglesortinghat.com";
const SUPPORT_EMAIL = "contact@mugglesortinghat.com";
const BENEFIT_KEYS = [
  "team.benefits.local",
  "team.benefits.personalized",
  "team.benefits.support",
] as const;
const SUGGESTED_QUESTION_KEYS = [
  "assistant.suggestions.q1",
  "assistant.suggestions.q2",
  "assistant.suggestions.q3",
  "assistant.suggestions.q4",
  "assistant.suggestions.q5",
] as const;
const getAuthHeaders = () => {
  let token = "";
  let timestamp = "";
  if (typeof window !== "undefined" && (window as any).clientInfo) {
    token = (window as any).clientInfo.tk || "";
    timestamp = (window as any).clientInfo.ts || "";
  }
  return {
    "x-token": token,
    "x-timestamp": timestamp,
  };
};

function App() {
  const [universities, setUniversities] = useState<University[]>([]);
  const [selectedUniversity, setSelectedUniversity] = useState<string>("");
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState<
    { role: "user" | "assistant"; content: string }[]
  >([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  // Add state for mobile university panel
  const [showUniversitiesMobile, setShowUniversitiesMobile] = useState(false);
  const [promptCounter, setPromptCounter] = useState<number>(0);
  const [showCounselorPopup, setShowCounselorPopup] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showAgentInfo, setShowAgentInfo] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [counselorPopupShown, setCounselorPopupShown] =
    useState<boolean>(false);
  const [showContactPopup, setShowContactPopup] = useState(false);
  // Add state for copy feedback (index of copied message)
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  // Ref for chat history container
  const chatHistoryRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();

  // Auto-scroll to bottom whenever chat history updates
  useEffect(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTo({
        top: chatHistoryRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [chatHistory, aiLoading]);

  // Helper to detect mobile
  const isMobile = () => window.innerWidth <= 768;

  // Close university panel on resize if not mobile
  useEffect(() => {
    const handleResize = () => {
      if (!isMobile()) setShowUniversitiesMobile(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const loadUniversities = async () => {
      try {
        const response = await fetch(apiUrl("/api/universities"), {
          headers: getAuthHeaders(),
        });
        const data = await response.json();
        setUniversities(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching universities:", error);
        setUniversities([]);
      }
    };

    loadUniversities();
  }, []);

  // Load counter and popup state from cookies on mount
  useEffect(() => {
    const count = parseInt(Cookies.get("promptCounter") || "0", 10);
    setPromptCounter(isNaN(count) ? 0 : count);
    const popupShown = Cookies.get("counselorPopupShown") === "true";
    setCounselorPopupShown(popupShown);
  }, []);

  useEffect(() => {
    // Check if timestamp is older than 15 minutes
    if (typeof window !== "undefined" && (window as any).clientInfo) {
      const ts = (window as any).clientInfo.ts;
      if (ts) {
        const now = Date.now();
        const tsNum = Number(ts);
        // If ts is a string timestamp, try to parse as int or Date
        let tsMillis = tsNum;
        if (tsNum < 1e12) {
          // If it's in seconds, convert to ms
          tsMillis = tsNum * 1000;
        }
        if (now - tsMillis > 15 * 60 * 1000) {
          window.location.reload();
        }
      }
    }
  }, []);

  const fetchCourses = async (shortName: string) => {
    setLoading(true);
    try {
      const response = await fetch(
        apiUrl(`/api/universities/${shortName}/courses`),
        { headers: getAuthHeaders() },
      );
      if (response.ok) {
        const data = await response.json();
        setCourses(Array.isArray(data) ? data : []);
      } else {
        setCourses([]);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      setCourses([]);
    }
    setLoading(false);
  };

  const handleUniversityChange = (shortName: string) => {
    setSelectedUniversity(shortName);
    if (shortName) {
      fetchCourses(shortName);
    } else {
      setCourses([]);
    }
  };

  const sendPrompt = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return false;

    const newCounter = promptCounter + 1;
    setPromptCounter(newCounter);
    Cookies.set("promptCounter", String(newCounter), { expires: 7 });

    if (
      newCounter > 1 &&
      !counselorPopupShown &&
      !showCounselorPopup &&
      !showContactForm &&
      !showAgentInfo
    ) {
      setShowCounselorPopup(true);
      setCounselorPopupShown(true);
      Cookies.set("counselorPopupShown", "true", { expires: 7 });
      return false;
    }

    setAiLoading(true);
    setChatHistory((prev) => [...prev, { role: "user", content: trimmed }]);
    try {
      const payload: any = { prompt: trimmed };
      if (threadId) payload.thread_id = threadId;
      const response = await fetch(apiUrl("/api/prompt"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      setChatHistory((prev) => [
        ...prev,
        { role: "assistant", content: data.response },
      ]);
      setThreadId(data.thread_id || threadId || null);
    } catch (error) {
      setChatHistory((prev) => [
        ...prev,
        { role: "assistant", content: t("assistant.error") },
      ]);
    } finally {
      setAiLoading(false);
    }

    return true;
  };

  const handlePromptSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (aiLoading) return;
    const currentPrompt = prompt.trim();
    if (!currentPrompt) return;

    const sent = await sendPrompt(currentPrompt);
    if (sent) {
      setPrompt("");
    }
  };

  const handleSuggestionClick = (
    key: (typeof SUGGESTED_QUESTION_KEYS)[number],
  ) => {
    if (aiLoading) return;
    const question = t(key);
    sendPrompt(question);
  };

  // Handle counselor popup answer
  const handleCounselorAnswer = (answer: boolean) => {
    setShowCounselorPopup(false);
    if (answer) {
      setShowContactForm(true);
    } else {
      setShowAgentInfo(true);
    }
  };

  // Handle contact form submit
  const handleContactFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactSubmitting(true);
    setContactError(null);
    try {
      const res = await fetch(apiUrl("/api/userinfo"), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(contactForm),
      });
      if (!res.ok) throw new Error("CONTACT_SUBMIT_FAILED");
      setContactSubmitted(true);
    } catch (err: any) {
      const key =
        err instanceof Error && err.message === "CONTACT_SUBMIT_FAILED"
          ? "contactForm.errors.failed"
          : "contactForm.errors.unknown";
      setContactError(t(key));
    } finally {
      setContactSubmitting(false);
    }
  };

  // Copy to clipboard handler
  const handleCopy = (content: string, idx: number) => {
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(content);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1200);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-toolbar">
          <LanguageSwitcher />
        </div>
        {/* Stack logo above the title, logo bigger (64px), keep original aspect ratio */}
        <img
          src={logo192}
          className="App-logo"
          alt={t("header.title")}
          style={{
            height: 64,
            display: "block",
            margin: "0 auto",
            padding: 0,
          }}
        />
        <h1 style={{ fontSize: "3rem", marginTop: 0 }}>{t("header.title")}</h1>
        <p>{t("header.subtitle")}</p>
      </header>

      <main className="App-main">
        <div className="container">
          {/* Mobile toggle button for universities */}
          <div
            className="mobile-toggle-universities"
            style={{ display: isMobile() ? "block" : "none", marginBottom: 16 }}
          >
            <button
              onClick={() => setShowUniversitiesMobile((v) => !v)}
              style={{
                padding: "0.75rem 1.5rem",
                fontSize: "1rem",
                borderRadius: 8,
                border: "1px solid #3498db",
                background: showUniversitiesMobile ? "#3498db" : "#fff",
                color: showUniversitiesMobile ? "#fff" : "#3498db",
                fontWeight: 600,
                cursor: "pointer",
                width: "100%",
                marginBottom: 8,
                boxShadow: "0 2px 8px rgba(52,152,219,0.07)",
                transition: "all 0.2s",
              }}
            >
              {showUniversitiesMobile
                ? t("universities.toggleHide")
                : t("universities.toggleShow")}
            </button>
          </div>
          <div className="side-by-side">
            <div
              className={`left-panel${isMobile() ? (showUniversitiesMobile ? " expanded" : "") : ""}`}
              style={
                isMobile() && !showUniversitiesMobile ? { display: "none" } : {}
              }
            >
              <section className="university-section">
                <h2>{t("universities.heading")}</h2>
                <div className="university-grid">
                  {Array.isArray(universities) &&
                    universities.map((university) => (
                      <div
                        key={university.id}
                        className={`university-card ${selectedUniversity === university.shortName ? "selected" : ""}`}
                        onClick={() =>
                          handleUniversityChange(university.shortName)
                        }
                      >
                        {/* Favicon icon for the university */}
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          {university.icon ? (
                            <img
                              src={`data:image/png;base64,${university.icon}`}
                              alt={university.shortName + " icon"}
                              style={{
                                width: 32,
                                height: 32,
                                objectFit: "contain",
                                borderRadius: 4,
                              }}
                            />
                          ) : null}
                          <h3 style={{ margin: 0 }}>{university.name}</h3>
                        </div>
                        <p className="short-name">{university.shortName}</p>
                        <a
                          href={university.homepage}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t("universities.visitWebsite")}
                        </a>
                      </div>
                    ))}
                </div>
              </section>
              {selectedUniversity && (
                <section className="section">
                  <h2>
                    {t("universities.coursesHeading", {
                      university: selectedUniversity,
                    })}
                  </h2>
                  {loading ? (
                    <div className="loading">{t("universities.loading")}</div>
                  ) : Array.isArray(courses) && courses.length > 0 ? (
                    <div className="course-grid">
                      {courses.map((course) => (
                        <div key={course.id} className="course-card">
                          <h3>{course.name}</h3>
                          <p>{course.description}</p>
                          <a
                            href={course.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t("universities.viewCourse")}
                          </a>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-courses">
                      {t("universities.empty")}
                    </div>
                  )}
                </section>
              )}
            </div>
            <div className="right-panel">
              <section className="section">
                <h2>{t("assistant.heading")}</h2>
                <div className="chat-container">
                  <div className="chat-history" ref={chatHistoryRef}>
                    {chatHistory.length === 0 && !aiLoading && (
                      <div className="suggested-questions">
                        <p className="suggested-questions-title">
                          {t("assistant.suggestions.title")}
                        </p>
                        <div className="suggested-question-list">
                          {SUGGESTED_QUESTION_KEYS.map((key) => (
                            <button
                              key={key}
                              type="button"
                              className="suggested-question-pill"
                              disabled={aiLoading}
                              onClick={() => handleSuggestionClick(key)}
                            >
                              {t(key)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {chatHistory.map((msg, idx) => (
                      <div key={idx} className={`chat-message ${msg.role}`}>
                        <div
                          className="chat-bubble"
                          style={{ position: "relative" }}
                        >
                          {msg.role === "assistant" ? (
                            <>
                              {/* Copy button for assistant responses */}
                              <button
                                className="copy-btn"
                                style={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                  background: "none",
                                  border: "none",
                                  color: "#3498db",
                                  cursor: "pointer",
                                  fontSize: "1.1em",
                                  padding: 0,
                                  zIndex: 2,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                                onClick={() => handleCopy(msg.content, idx)}
                                aria-label={t("assistant.copyAria")}
                              >
                                {copiedIdx === idx ? (
                                  <span
                                    style={{
                                      color: "#2980b9",
                                      fontWeight: 600,
                                      fontSize: "0.98em",
                                    }}
                                  >
                                    {t("assistant.copied")}
                                  </span>
                                ) : (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="22"
                                    height="22"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{ display: "block" }}
                                  >
                                    <rect
                                      x="9"
                                      y="9"
                                      width="13"
                                      height="13"
                                      rx="2"
                                      ry="2"
                                    />
                                    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                                  </svg>
                                )}
                              </button>
                              <ReactMarkdown>{msg.content}</ReactMarkdown>
                            </>
                          ) : (
                            msg.content
                          )}
                        </div>
                      </div>
                    ))}
                    {aiLoading && (
                      <div className="chat-message assistant">
                        <div className="chat-bubble">
                          <span className="breathing-dot" />
                          {t("assistant.thinking")}
                        </div>
                      </div>
                    )}
                  </div>
                  <form
                    onSubmit={handlePromptSubmit}
                    className="prompt-form"
                    style={{ marginTop: "1rem" }}
                  >
                    <textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder={t("assistant.placeholder")}
                      rows={2}
                      className="prompt-input"
                      disabled={aiLoading}
                    />
                    <button
                      type="submit"
                      disabled={aiLoading || !prompt.trim()}
                      className="submit-btn"
                    >
                      {t("buttons.send")}
                    </button>
                  </form>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      {/* Counselor popup/modal */}
      {showCounselorPopup && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{t("counselor.question")}</h2>
            <div style={{ display: "flex", gap: 16, marginTop: 24 }}>
              <button
                onClick={() => handleCounselorAnswer(true)}
                className="submit-btn"
              >
                {t("buttons.yes")}
              </button>
              <button
                onClick={() => handleCounselorAnswer(false)}
                className="submit-btn"
              >
                {t("buttons.no")}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Contact form modal */}
      {showContactForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{t("contactForm.title")}</h2>
            {contactSubmitted ? (
              <div>
                <p>{t("contactForm.success")}</p>
                <button
                  onClick={() => setShowContactForm(false)}
                  className="submit-btn"
                >
                  {t("buttons.close")}
                </button>
              </div>
            ) : (
              <form onSubmit={handleContactFormSubmit}>
                <input
                  type="text"
                  placeholder={t("contactForm.fields.name")}
                  value={contactForm.name}
                  onChange={(e) =>
                    setContactForm({ ...contactForm, name: e.target.value })
                  }
                  required
                />
                <input
                  type="email"
                  placeholder={t("contactForm.fields.email")}
                  value={contactForm.email}
                  onChange={(e) =>
                    setContactForm({ ...contactForm, email: e.target.value })
                  }
                  required
                />
                <input
                  type="text"
                  placeholder={t("contactForm.fields.phone")}
                  value={contactForm.phone}
                  onChange={(e) =>
                    setContactForm({ ...contactForm, phone: e.target.value })
                  }
                  required
                />
                <textarea
                  placeholder={t("contactForm.fields.message")}
                  value={contactForm.message}
                  onChange={(e) =>
                    setContactForm({ ...contactForm, message: e.target.value })
                  }
                  rows={3}
                />
                {contactError && <div className="error">{contactError}</div>}
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={contactSubmitting}
                >
                  {contactSubmitting
                    ? t("buttons.submitting")
                    : t("buttons.submit")}
                </button>
                <button
                  type="button"
                  className="submit-btn"
                  onClick={() => setShowContactForm(false)}
                  style={{ marginLeft: 8 }}
                >
                  {t("buttons.cancel")}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
      {/* Agent info modal */}
      {showAgentInfo && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{t("team.meetTitle")}</h2>
            <p>{t("team.intro")}</p>
            <h3>{t("team.whyTitle")}</h3>
            <ul style={{ textAlign: "left", margin: "0 0 1rem 1.5rem" }}>
              {BENEFIT_KEYS.map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ul>
            <h3>{t("team.contactSenior")}</h3>
            <p>
              <b>{t("team.counselorName")}</b>
            </p>
            <p>{t("team.mobileLabel", { phone: COUNSELOR_PHONE })}</p>
            <p>
              {t("team.emailLabel")}{" "}
              <a href={`mailto:${COUNSELOR_EMAIL}`}>{COUNSELOR_EMAIL}</a>
            </p>
            <p>{t("team.closing")}</p>
            <button
              onClick={() => setShowAgentInfo(false)}
              className="submit-btn"
            >
              {t("buttons.close")}
            </button>
          </div>
        </div>
      )}

      {/* Contact info popup modal (footer trigger) */}
      {showContactPopup && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{t("contactPopup.title")}</h2>
            <p>{t("team.intro")}</p>
            <h3>{t("team.whyTitle")}</h3>
            <ul style={{ textAlign: "left", margin: "0 0 1rem 1.5rem" }}>
              {BENEFIT_KEYS.map((key) => (
                <li key={`contact-${key}`}>{t(key)}</li>
              ))}
            </ul>
            <h3>{t("team.contactSenior")}</h3>
            <p>
              <b>{t("team.counselorName")}</b>
            </p>
            <p>{t("team.mobileLabel", { phone: COUNSELOR_PHONE })}</p>
            <p>
              {t("team.emailLabel")}{" "}
              <a href={`mailto:${COUNSELOR_EMAIL}`}>{COUNSELOR_EMAIL}</a>
            </p>
            <p>{t("team.closing")}</p>
            <hr style={{ margin: "1.5rem 0" }} />
            <div style={{ fontSize: "0.97rem", color: "#555", marginTop: 8 }}>
              <b>{t("team.feedbackTitle")}</b>
              <br />
              {t("team.feedbackPrompt")}{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </div>
            <button
              onClick={() => setShowContactPopup(false)}
              className="submit-btn"
            >
              {t("buttons.close")}
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer
        className="App-footer"
        style={{ textAlign: "center", padding: "1.5rem 0", marginTop: 32 }}
      >
        <span>
          <span style={{ color: "#E0E0E0", fontWeight: 500 }}>
            {t("footer.questions")}
          </span>
          <br />
          <button
            style={{
              color: "#90caf9",
              background: "none",
              border: "none",
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: 600,
              marginTop: 4,
            }}
            onClick={() => setShowContactPopup(true)}
          >
            {t("footer.contact")}
          </button>
        </span>
      </footer>
    </div>
  );
}

export default App;
